import { useId, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import './scenes.css';

/**
 * Pieces shared by the drawn help scenes. The scenes are illustrations, so
 * colours here are literal: they mirror the redesigned kit's tokens
 * (kit-next.css) -- the panel is always light (`:root` values) and the editor
 * always dark (the `.dark` values) -- and the "photos" are drawn rooms.
 * Radii are the kit's at the scenes' ~0.5 scale: button 12 -> 6, chip 6 -> 3,
 * control 7.2 -> 3.6, card 12 -> 6, panel 14.4 -> 7.
 */

/** Light panel (the pages). */
export const L = {
  canvas: '#eeefed',
  surface: '#ffffff',
  sunken: '#e4e5e2',
  hover: '#f5f6f4',
  line: 'rgba(17,18,20,.08)',
  lineStrong: 'rgba(17,18,20,.15)',
  ink: '#111214',
  ink2: '#4a4d4b',
  ink3: '#646765',
  brand: '#12988c',
  brandInk: '#0b6e66',
  brandSubtle: '#ddf4f1',
  success: '#457010',
  successSubtle: '#edf4e1',
  warning: '#a15c07',
  warningSubtle: '#fbf0df',
  danger: '#c2362b',
  dangerSubtle: '#fbe9e7',
} as const;

/** Dark editor (and the black sidebar, which re-scopes the same tokens). */
export const D = {
  bg: '#0d0e0f',
  surface: '#17191a',
  /** Floating glass base (`--editor-glass`, drawn at 87 %) and the opaque right panel. */
  glass: '#111214',
  panel: '#111214',
  raised: '#17191a',
  sunken: '#111213',
  hover: 'rgba(255,255,255,.06)',
  line: 'rgba(255,255,255,.09)',
  lineStrong: 'rgba(255,255,255,.16)',
  ink: '#f2f3f1',
  ink2: '#b9bdba',
  ink3: '#8f9390',
  brand: '#3dd6c7',
  success: '#a3d977',
  warning: '#f2b45a',
  warningSubtle: 'rgba(242,180,90,.16)',
} as const;

const ICONS: Record<string, ReactNode> = {
  plus: <path d="M5 12h14M12 5v14" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  check: <path d="M20 6 9 17l-5-5" />,
  down: <path d="m6 9 6 6 6-6" />,
  right: <path d="m9 18 6-6-6-6" />,
  eye: (
    <>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </>
  ),
  card: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 7L2 7" />
    </>
  ),
  trash: <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />,
  pencil: <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />,
  copy: (
    <>
      <rect x="8" y="8" width="14" height="14" rx="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5M12 7v5l4 2" />
    </>
  ),
  dots: (
    <>
      <circle cx="5" cy="12" r="1.3" />
      <circle cx="12" cy="12" r="1.3" />
      <circle cx="19" cy="12" r="1.3" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </>
  ),
  upload: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />,
  refresh: <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />,
  link: <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />,
  folder: <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.7-.9l-.8-1.2A2 2 0 0 0 7.9 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />,
  play: <path d="M6 4v16l14-8Z" />,
  bell: <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0" />,
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM20 14v3M17 20h4" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01" />
    </>
  ),
};

export type IconName = keyof typeof ICONS | (string & {});

/** A 24-unit stroke icon drawn at `size`. */
export function Ico({
  name,
  x,
  y,
  size = 10,
  color = L.ink2,
  sw = 2,
  fill = 'none',
}: {
  name: IconName;
  x: number;
  y: number;
  size?: number;
  color?: string;
  sw?: number;
  fill?: string;
}) {
  return (
    <g
      transform={`translate(${x} ${y}) scale(${size / 24})`}
      fill={fill}
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICONS[name]}
    </g>
  );
}

type TextProps = Omit<React.SVGProps<SVGTextElement>, 'fontSize' | 'fontWeight'> & {
  s?: number;
  w?: number;
  c?: string;
};

/** Text with a size, weight and colour shorthand. */
export function Tx({ s = 7, w = 400, c = L.ink, children, ...rest }: TextProps) {
  return (
    <text fontSize={s} fontWeight={w} fill={c} {...rest}>
      {children}
    </text>
  );
}

type BtnKind = 'primary' | 'secondary' | 'ghost' | 'disabled' | 'dangerOutline';

/**
 * A kit-looking button (12px radius -> 6): black `primary` with the ink
 * sheen (flat light ink in the dark editor), `secondary` with a strong line
 * and a soft lift, quiet `ghost`. `disabled` is the primary at 45 %.
 */
export function Btn({
  x,
  y,
  w,
  h = 15,
  label,
  kind = 'secondary',
  icon,
  s = 6.4,
  dark = false,
  className,
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
  label?: string;
  kind?: BtnKind;
  icon?: IconName;
  s?: number;
  dark?: boolean;
  className?: string;
}) {
  const ink = kind === 'primary' || kind === 'disabled';
  const fill = ink ? (dark ? D.ink : L.ink) : dark ? D.surface : L.surface;
  const stroke = kind === 'secondary' ? (dark ? D.lineStrong : L.lineStrong) : kind === 'dangerOutline' ? 'rgba(194,54,43,.45)' : 'none';
  const text = ink ? (dark ? D.bg : '#ffffff') : kind === 'dangerOutline' ? L.danger : kind === 'ghost' ? (dark ? D.ink2 : L.ink2) : dark ? D.ink : L.ink;
  const iconW = icon ? 11 : 0;
  const labelW = label ? label.length * s * 0.52 : 0;
  const start = x + (w - iconW - labelW) / 2;
  return (
    <g className={className} opacity={kind === 'disabled' ? 0.45 : 1}>
      {kind === 'primary' && !dark && <rect x={x} y={y + 1.2} width={w} height={h} rx={6} fill="#111214" opacity=".18" />}
      {kind === 'secondary' && !dark && <rect x={x} y={y + 0.6} width={w} height={h} rx={6} fill="#111214" opacity=".05" />}
      <rect x={x} y={y} width={w} height={h} rx={6} fill={kind === 'ghost' || kind === 'dangerOutline' ? 'none' : fill} stroke={stroke} />
      {ink && !dark && <rect x={x} y={y} width={w} height={h} rx={6} fill="url(#hs-ink-sheen)" />}
      {ink && !dark && <path d={`M${x + 5} ${y + 0.6} H${x + w - 5}`} stroke="#ffffff" strokeOpacity=".24" />}
      {icon && <Ico name={icon} x={start} y={y + (h - 8) / 2} size={8} color={text} sw={2.2} />}
      {label && (
        <Tx x={start + iconW} y={y + h / 2 + s * 0.35} s={s} w={500} c={text}>
          {label}
        </Tx>
      )}
    </g>
  );
}

/** Pointer arrow; its tip is the group origin. */
export function Cursor({ className }: { className: string }) {
  return (
    <g className={`${className} hs-cursor`} opacity={0}>
      <path
        d="M0 0 L0 15 L4.2 11.2 L7.2 17.4 L9.8 16.2 L6.8 10.2 L12.2 9.9 Z"
        fill="#111111"
        stroke="#ffffff"
        strokeWidth={1.3}
        strokeLinejoin="round"
      />
    </g>
  );
}

/** Click ripple centred on (x, y). */
export function Ripple({ x, y, className }: { x: number; y: number; className: string }) {
  return <circle className={`${className} hs-ripple`} cx={x} cy={y} r={10} />;
}

/** Drop shadow used by floating drawn surfaces; render once per scene. */
export function ScenePopFilter({ id }: { id: string }) {
  return (
    <filter id={id} x="-10%" y="-10%" width="120%" height="140%">
      <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity=".28" />
    </filter>
  );
}

/** Wraps the shared root attributes of a scene (aria label, viewBox, font). */
export function SceneSvg({ label, css, children }: { label: string; css: string; children: ReactNode }) {
  return (
    <svg viewBox="0 0 640 360" role="img" aria-label={label} className="block h-auto w-full" fontFamily="inherit">
      <style>{css}</style>
      <defs>
        {/* ink sheen of primary buttons / active tabs (`--ink-sheen`) */}
        <linearGradient id="hs-ink-sheen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity=".16" />
          <stop offset=".55" stopColor="#ffffff" stopOpacity=".03" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        {/* black material of the sidebar / editor right panel (170deg, top-left) */}
        <linearGradient id="hs-black-sheen" x1="0.08" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity=".07" />
          <stop offset=".22" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="hs-wash-a" cx="0.82" cy="0" r="0.7">
          <stop offset="0" stopColor="#2ec4b6" stopOpacity=".13" />
          <stop offset="1" stopColor="#2ec4b6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hs-wash-b" cx="0.1" cy="1" r="0.7">
          <stop offset="0" stopColor="#2ec4b6" stopOpacity=".08" />
          <stop offset="1" stopColor="#2ec4b6" stopOpacity="0" />
        </radialGradient>
        <filter id="hs-e4" x="-15%" y="-15%" width="130%" height="140%" colorInterpolationFilters="sRGB">
          <feDropShadow dx="0" dy="6" stdDeviation="7" floodColor="#111214" floodOpacity=".26" />
        </filter>
        <filter id="hs-glass-shadow" x="-10%" y="-20%" width="120%" height="150%" colorInterpolationFilters="sRGB">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity=".4" />
        </filter>
        <filter id="hs-e1" x="-5%" y="-10%" width="110%" height="130%" colorInterpolationFilters="sRGB">
          <feDropShadow dx="0" dy="0.6" stdDeviation="0.6" floodColor="#111214" floodOpacity=".06" />
        </filter>
      </defs>
      {children}
    </svg>
  );
}

/** Unique, selector-safe prefix for ids inside one scene instance. */
export function useUid(): string {
  return useId().replace(/[^a-zA-Z0-9]/g, '');
}

/* ------------------------------------------------------------------ */
/* Drawn "photos": bright rooms, never dark.                           */

/**
 * The living room of the connect demo, in a 488x328 box. `bleedLeft` /
 * `bleedTop` extend the room past that box (same scale, same anchors) so the
 * editor scenes can run it edge to edge under the floating chrome.
 */
export function LivingRoom({ x, y, w, h, bleedLeft = 0, bleedTop = 0 }: { x: number; y: number; w: number; h: number; bleedLeft?: number; bleedTop?: number }) {
  const x0 = 32 - bleedLeft;
  const y0 = 32 - bleedTop;
  const u = useUid();
  const id = (n: string) => `${n}-${u}`;
  const ref = (n: string) => `url(#${id(n)})`;
  return (
    <svg x={x - bleedLeft} y={y - bleedTop} width={w + bleedLeft} height={h + bleedTop} viewBox={`${x0} ${y0} ${488 + bleedLeft} ${328 + bleedTop}`} preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={id('ceil')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f6f3ee" />
          <stop offset="1" stopColor="#ebe5dc" />
        </linearGradient>
        <linearGradient id={id('back')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f3eee7" />
          <stop offset="1" stopColor="#e6dfd4" />
        </linearGradient>
        <linearGradient id={id('left')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ddd5ca" />
          <stop offset="1" stopColor="#e9e3da" />
        </linearGradient>
        <linearGradient id={id('right')} x1="1" y1="0" x2="0" y2="0">
          <stop offset="0" stopColor="#d8d0c4" />
          <stop offset="1" stopColor="#e5ded3" />
        </linearGradient>
        <linearGradient id={id('floor')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#c9a47e" />
          <stop offset="1" stopColor="#a87e57" />
        </linearGradient>
        <linearGradient id={id('sky')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a9d0ef" />
          <stop offset="1" stopColor="#e3f0f8" />
        </linearGradient>
        <linearGradient id={id('kitchen')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fffaf2" />
          <stop offset="1" stopColor="#efe5d6" />
        </linearGradient>
        <radialGradient id={id('sun')} cx="0.3" cy="0.6" r="0.7">
          <stop offset="0" stopColor="#fff6e3" stopOpacity=".55" />
          <stop offset="1" stopColor="#fff6e3" stopOpacity="0" />
        </radialGradient>
        <clipPath id={id('door')}>
          <rect x="330" y="140" width="46" height="98" />
        </clipPath>
      </defs>
      <path d={`M${x0} ${y0} H520 V50 Q470 70 410 96 H170 Q90 66 ${x0} 44 Z`} fill={ref('ceil')} />
      <rect x="170" y="96" width="240" height="142" fill={ref('back')} />
      <path d={`M${x0} 44 Q90 66 170 96 V238 Q100 262 ${x0} 300 Z`} fill={ref('left')} />
      <path d="M520 50 Q470 70 410 96 V238 Q470 262 520 296 Z" fill={ref('right')} />
      <path d={`M${x0} 360 V300 Q100 262 170 238 H410 Q470 262 520 296 V360 Z`} fill={ref('floor')} />
      <path
        d="M190 238 L80 360 M215 238 L150 360 M240 238 L215 360 M265 238 L270 360 M290 238 L325 360 M315 238 L380 360 M340 238 L440 360 M365 238 L500 360 M390 238 L520 340"
        stroke="#7a5638"
        strokeOpacity=".14"
      />
      <path d="M170 96 V238 M410 96 V238" stroke="#cfc6b9" />
      <path d={`M${x0} 300 Q100 262 170 238 H410 Q470 262 520 296`} stroke="#f7f4ef" strokeWidth="3" fill="none" />
      <path d="M54 92 Q100 108 146 122 V214 Q100 226 54 244 Z" fill={ref('sky')} />
      <path d="M54 196 Q100 196 146 190 V214 Q100 226 54 244 Z" fill="#cfe0cf" opacity=".7" />
      <path d="M54 92 Q100 108 146 122 V214 Q100 226 54 244 Z M100 106 V229" stroke="#ffffff" strokeWidth="3" fill="none" />
      <path d="M58 300 L150 252 L230 262 L150 330 Z" fill={ref('sun')} />
      <ellipse cx="290" cy="62" rx="16" ry="4" fill="#ffffff" stroke="#e1dad0" />
      <rect x="196" y="122" width="64" height="44" fill="#d9e3df" stroke="#3a3632" strokeWidth="2" />
      <path d="M200 160 Q220 136 236 150 T256 142 V162 H200 Z" fill="#a9bdb4" />
      <g clipPath={ref('door')}>
        <rect x="330" y="140" width="46" height="98" fill={ref('kitchen')} />
        <rect x="334" y="152" width="38" height="18" fill="#e5e0d8" stroke="#d6cfc4" />
        <rect x="330" y="204" width="46" height="34" fill="#d9d3ca" />
        <rect x="330" y="200" width="46" height="5" fill="#bfb8ad" />
        <path d="M330 238 L376 238 L376 232 L330 236 Z" fill="#c29c76" />
      </g>
      <rect x="330" y="140" width="46" height="98" fill="none" stroke="#ffffff" strokeWidth="3" />
      <path d="M160 262 V152" stroke="#3a3632" strokeWidth="2" />
      <ellipse cx="160" cy="264" rx="9" ry="2.5" fill="#3a3632" />
      <path d="M150 152 L170 152 L166 136 L154 136 Z" fill="#f4ede1" stroke="#d9cfbf" />
      <ellipse cx="252" cy="276" rx="112" ry="24" fill="#ede7de" />
      <rect x="186" y="200" width="122" height="40" rx="7" fill="#b9b1a6" />
      <rect x="192" y="186" width="54" height="24" rx="6" fill="#c8c1b7" />
      <rect x="248" y="186" width="54" height="24" rx="6" fill="#c8c1b7" />
      <rect x="180" y="198" width="14" height="40" rx="6" fill="#aea69a" />
      <rect x="300" y="198" width="14" height="40" rx="6" fill="#aea69a" />
      <rect x="214" y="198" width="18" height="14" rx="4" fill="#d9c7a7" />
      <path d="M222 274 V286 M282 274 V286" stroke="#5f4630" strokeWidth="2.5" />
      <ellipse cx="252" cy="272" rx="42" ry="8" fill="#8a6a4c" />
      <ellipse cx="252" cy="270.5" rx="42" ry="7" fill="#9b7957" />
      <rect x="430" y="248" width="24" height="28" rx="3" fill="#e2d9cc" />
      <circle cx="442" cy="232" r="15" fill="#6c8c5c" />
      <circle cx="431" cy="222" r="10" fill="#7d9e6a" />
      <circle cx="454" cy="224" r="11" fill="#5f7f50" />
      <circle cx="444" cy="212" r="8" fill="#86a874" />
    </svg>
  );
}

/** A bright kitchen-dining room, in a 488x328 box (`bleedLeft` / `bleedTop`: see `LivingRoom`). */
export function DiningRoom({ x, y, w, h, bleedLeft = 0, bleedTop = 0 }: { x: number; y: number; w: number; h: number; bleedLeft?: number; bleedTop?: number }) {
  const x0 = -bleedLeft;
  const y0 = -bleedTop;
  const u = useUid();
  const id = (n: string) => `${n}-${u}`;
  const ref = (n: string) => `url(#${id(n)})`;
  return (
    <svg x={x - bleedLeft} y={y - bleedTop} width={w + bleedLeft} height={h + bleedTop} viewBox={`${x0} ${y0} ${488 + bleedLeft} ${328 + bleedTop}`} preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={id('ceil')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f8f7f4" />
          <stop offset="1" stopColor="#ece9e3" />
        </linearGradient>
        <linearGradient id={id('wall')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#e9e6e0" />
          <stop offset="1" stopColor="#f4f2ee" />
        </linearGradient>
        <linearGradient id={id('side')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#d9d5cd" />
          <stop offset="1" stopColor="#e8e5de" />
        </linearGradient>
        <linearGradient id={id('floor')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d9d4cb" />
          <stop offset="1" stopColor="#bdb6aa" />
        </linearGradient>
        <linearGradient id={id('sky')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#b6d8f1" />
          <stop offset="1" stopColor="#eaf4fa" />
        </linearGradient>
        <radialGradient id={id('sun')} cx="0.5" cy="0.4" r="0.7">
          <stop offset="0" stopColor="#fffaf0" stopOpacity=".6" />
          <stop offset="1" stopColor="#fffaf0" stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d={`M${x0} ${y0} H488 V16 Q440 38 380 64 H110 Q50 38 ${x0} 14 Z`} fill={ref('ceil')} />
      <rect x="110" y="64" width="270" height="160" fill={ref('wall')} />
      <path d={`M${x0} 14 Q50 38 110 64 V224 Q50 250 ${x0} 286 Z`} fill={ref('side')} />
      <path d="M488 16 Q440 38 380 64 V224 Q440 250 488 282 Z" fill={ref('side')} />
      <path d={`M${x0} 328 V286 Q50 250 110 224 H380 Q440 250 488 282 V328 Z`} fill={ref('floor')} />
      <path d="M110 64 V224 M380 64 V224" stroke="#cdc8bf" />
      <path d={`M${x0} 286 Q50 250 110 224 H380 Q440 250 488 282`} stroke="#faf9f6" strokeWidth="3" fill="none" />
      {/* window on the right wall */}
      <path d="M404 78 Q430 62 464 52 V196 Q430 206 404 218 Z" fill={ref('sky')} />
      <path d="M404 78 Q430 62 464 52 V196 Q430 206 404 218 Z M434 64 V204" stroke="#ffffff" strokeWidth="3" fill="none" />
      <path d="M330 250 L420 232 L470 262 L380 300 Z" fill={ref('sun')} />
      {/* kitchen run on the back wall */}
      <rect x="132" y="82" width="104" height="44" rx="2" fill="#fbfaf8" stroke="#d8d3ca" />
      <rect x="240" y="82" width="104" height="44" rx="2" fill="#fbfaf8" stroke="#d8d3ca" />
      <path d="M184 82 V126 M292 82 V126" stroke="#d8d3ca" />
      <rect x="132" y="150" width="212" height="74" fill="#f4f2ee" stroke="#d8d3ca" />
      <path d="M185 150 V224 M238 150 V224 M291 150 V224" stroke="#d8d3ca" />
      <rect x="128" y="144" width="220" height="8" rx="1" fill="#cfc8bb" />
      <rect x="150" y="132" width="60" height="12" fill="#e5e1d9" />
      <rect x="254" y="118" width="20" height="26" rx="3" fill="#b9b4aa" />
      <rect x="296" y="124" width="26" height="20" rx="3" fill="#8e8a82" opacity=".75" />
      <circle cx="170" cy="164" r="1.8" fill="#9d978c" />
      <circle cx="223" cy="164" r="1.8" fill="#9d978c" />
      <circle cx="276" cy="164" r="1.8" fill="#9d978c" />
      {/* dining table and chairs */}
      <ellipse cx="250" cy="288" rx="110" ry="26" fill="#efebe3" />
      <rect x="160" y="238" width="180" height="12" rx="3" fill="#d2a97b" />
      <path d="M170 250 L162 292 M330 250 L338 292 M200 250 V280 M300 250 V280" stroke="#a37c52" strokeWidth="5" />
      <ellipse cx="250" cy="240" rx="92" ry="12" fill="#e2bc8f" opacity=".7" />
      <rect x="236" y="222" width="12" height="18" rx="2" fill="#ffffff" stroke="#d8d3ca" />
      <rect x="252" y="228" width="16" height="10" rx="5" fill="#9bb7a2" />
      <g fill="#ffffff" stroke="#d3cec4">
        <rect x="132" y="242" width="30" height="46" rx="9" />
        <rect x="338" y="242" width="30" height="46" rx="9" />
        <rect x="190" y="266" width="38" height="40" rx="10" />
        <rect x="274" y="266" width="38" height="40" rx="10" />
      </g>
      <path d="M200 306 v10 M216 306 v10 M284 306 v10 M300 306 v10" stroke="#c9c3b8" strokeWidth="2" />
      <ellipse cx="250" cy="50" rx="18" ry="4" fill="#ffffff" stroke="#e4dfd6" />
      <rect x="20" y="236" width="22" height="30" rx="3" fill="#e2d9cc" />
      <circle cx="31" cy="222" r="14" fill="#6c8c5c" />
      <circle cx="21" cy="212" r="9" fill="#7d9e6a" />
      <circle cx="42" cy="214" r="10" fill="#5f7f50" />
    </svg>
  );
}

export type EquirectKind = 'living' | 'kitchen' | 'bedroom';

/** A 2:1 equirectangular thumbnail of a bright room (what a 360° image looks like flat). */
export function EquirectThumb({ kind, x, y, w, h }: { kind: EquirectKind; x: number; y: number; w: number; h: number }) {
  const u = useUid();
  const id = (n: string) => `${n}-${u}`;
  const ref = (n: string) => `url(#${id(n)})`;
  const floor = kind === 'bedroom' ? ['#d8bf9b', '#c4a47c'] : kind === 'kitchen' ? ['#d9d6d0', '#bcb8b0'] : ['#c9a47e', '#a87e57'];
  const wall = kind === 'bedroom' ? '#e9e4de' : kind === 'kitchen' ? '#f3f2ee' : '#e6dfd3';
  return (
    <svg x={x} y={y} width={w} height={h} viewBox="0 0 200 100" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={id('floor')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={floor[0]} />
          <stop offset="1" stopColor={floor[1]} />
        </linearGradient>
        <linearGradient id={id('sky')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a9d0ef" />
          <stop offset="1" stopColor="#e6f2f9" />
        </linearGradient>
        <linearGradient id={id('ceil')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fbfaf7" />
          <stop offset="1" stopColor="#ebe6dd" />
        </linearGradient>
      </defs>
      <rect width="200" height="100" fill={ref('ceil')} />
      <path d="M0 22 Q50 30 100 24 T200 22 V68 Q150 76 100 70 T0 68 Z" fill={wall} />
      <path d="M0 68 Q50 76 100 70 T200 68 V100 H0 Z" fill={ref('floor')} />
      <path d="M0 68 Q50 76 100 70 T200 68" stroke="#ffffff" strokeWidth="2" fill="none" opacity=".85" />
      {kind === 'living' && (
        <>
          <path d="M16 32 Q36 38 56 34 V62 Q36 66 16 62 Z" fill={ref('sky')} />
          <path d="M36 35 V64" stroke="#fff" strokeWidth="1.6" />
          <rect x="84" y="46" width="52" height="18" rx="5" fill="#b9b1a6" />
          <rect x="88" y="40" width="22" height="10" rx="4" fill="#c8c1b7" />
          <rect x="112" y="40" width="22" height="10" rx="4" fill="#c8c1b7" />
          <ellipse cx="110" cy="76" rx="26" ry="5" fill="#ede7de" />
          <rect x="146" y="34" width="22" height="30" fill="#f2eee6" stroke="#d6cfc4" />
          <circle cx="184" cy="56" r="8" fill="#6c8c5c" />
          <rect x="180" y="60" width="8" height="8" fill="#e2d9cc" />
        </>
      )}
      {kind === 'kitchen' && (
        <>
          <rect x="22" y="30" width="70" height="12" fill="#fbfaf8" stroke="#d8d3ca" strokeWidth=".6" />
          <rect x="22" y="48" width="70" height="20" fill="#f4f2ee" stroke="#d8d3ca" strokeWidth=".6" />
          <rect x="20" y="46" width="74" height="3" fill="#cfc8bb" />
          <path d="M108 30 Q128 36 148 32 V62 Q128 66 108 62 Z" fill={ref('sky')} />
          <path d="M128 33 V64" stroke="#fff" strokeWidth="1.6" />
          <rect x="160" y="40" width="26" height="26" rx="2" fill="#dcdcdc" stroke="#c4c4c4" strokeWidth=".6" />
          <ellipse cx="70" cy="80" rx="30" ry="6" fill="#f0ede7" />
          <rect x="42" y="68" width="56" height="5" rx="2" fill="#d2a97b" />
        </>
      )}
      {kind === 'bedroom' && (
        <>
          <rect x="70" y="36" width="64" height="12" rx="3" fill="#c9a77f" />
          <rect x="66" y="46" width="72" height="18" rx="5" fill="#fbfaf8" stroke="#e1ddd5" strokeWidth=".6" />
          <rect x="72" y="42" width="22" height="9" rx="4" fill="#ffffff" stroke="#e1ddd5" strokeWidth=".6" />
          <rect x="108" y="42" width="22" height="9" rx="4" fill="#ffffff" stroke="#e1ddd5" strokeWidth=".6" />
          <rect x="66" y="55" width="72" height="9" rx="3" fill="#a9bdb4" />
          <path d="M16 30 Q36 36 56 32 V62 Q36 66 16 62 Z" fill={ref('sky')} />
          <path d="M10 28 V64 M62 28 V64" stroke="#f1ece4" strokeWidth="6" />
          <rect x="150" y="48" width="20" height="16" fill="#d2a97b" stroke="#b78b5d" strokeWidth=".6" />
          <ellipse cx="102" cy="78" rx="34" ry="6" fill="#efe9df" />
        </>
      )}
    </svg>
  );
}

/** An `EquirectThumb` with rounded corners (cards, pickers): clipped by a rect of radius `rx`. */
export function RoundedThumb({ kind, x, y, w, h, rx = 3.6 }: { kind: EquirectKind; x: number; y: number; w: number; h: number; rx?: number }) {
  const u = useUid();
  return (
    <g>
      <clipPath id={`rt-${u}`}>
        <rect x={x} y={y} width={w} height={h} rx={rx} />
      </clipPath>
      <g clipPath={`url(#rt-${u})`}>
        <EquirectThumb kind={kind} x={x} y={y} w={w} h={h} />
      </g>
    </g>
  );
}

type PillTone = 'neutral' | 'success' | 'warning' | 'brand';
const PILL: Record<PillTone, [bg: string, fg: string]> = {
  neutral: [L.sunken, L.ink2],
  success: [L.successSubtle, L.success],
  warning: [L.warningSubtle, L.warning],
  brand: [L.brandSubtle, L.brandInk],
};

/** The kit's `Badge`: a 6px-radius chip; success and warning carry the status dot (`dot` adds it to any tone). */
export function Pill({ x, y, label, tone = 'neutral', s = 5.2, dot }: { x: number; y: number; label: string; tone?: PillTone; s?: number; dot?: boolean }) {
  const [bg, fg] = PILL[tone];
  const withDot = dot ?? (tone === 'success' || tone === 'warning');
  const lead = withDot ? 6 : 0;
  const w = label.length * s * 0.54 + 9 + lead;
  return (
    <g>
      <rect x={x} y={y} width={w} height={s + 5.5} rx={3} fill={bg} />
      {withDot && <circle cx={x + 6} cy={y + (s + 5.5) / 2} r="1.5" fill={fg} />}
      <Tx x={x + lead + (w - lead) / 2} y={y + s + 1.2} textAnchor="middle" s={s} w={600} c={fg}>{label}</Tx>
    </g>
  );
}

/** Success toast of the panel (`Toaster` is top-right, tinted by type). */
export function PanelToast({ label, className, right = 628, y = 34 }: { label: string; className: string; right?: number; y?: number }) {
  const w = label.length * 3.7 + 34;
  const x = right - w;
  return (
    <g className={className} opacity={0} filter="url(#hs-e4)">
      <rect x={x} y={y} width={w} height="20" rx="6" fill={L.successSubtle} stroke={L.success} strokeOpacity=".3" />
      <circle cx={x + 11} cy={y + 10} r="4.2" fill="none" stroke={L.success} strokeWidth="1" />
      <path d={`M${x + 9} ${y + 10} l1.4 1.4 l2.4 -2.8`} fill="none" stroke={L.success} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <Tx x={x + 20} y={y + 12.6} s={6.4} w={500}>{label}</Tx>
    </g>
  );
}

/** A white dialog (card radius, e4 shadow) over a dimmed page, like the kit's `Dialog`. */
export function DialogFrame({
  x,
  y,
  w,
  h,
  title,
  children,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  children?: ReactNode;
}) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="6" fill={L.surface} stroke={L.line} filter="url(#hs-e4)" />
      <Tx x={x + 16} y={y + 22} s={8} w={600}>{title}</Tx>
      <Ico name="x" x={x + w - 18} y={y + 10} size={7} color={L.ink3} sw={2.2} />
      {children}
    </g>
  );
}

/** Labelled text field: label above, input below. `value`/`placeholder` are drawn by the caller. */
export function Field({ x, y, w, label, focus = false, children }: { x: number; y: number; w: number; label: string; focus?: boolean; children?: ReactNode }) {
  return (
    <g>
      <Tx x={x} y={y} s={5.8} w={500}>{label}</Tx>
      <rect x={x} y={y + 4} width={w} height="16" rx="3.6" fill={L.surface} stroke={focus ? L.brand : L.lineStrong} strokeWidth={focus ? 1.2 : 1} />
      {focus && <rect x={x - 1.2} y={y + 2.8} width={w + 2.4} height="18.4" rx="4.8" fill="none" stroke={L.brand} strokeOpacity=".3" strokeWidth="1.6" />}
      {children}
    </g>
  );
}

/** The kit's `Switch`: turquoise track when on, white knob (track is the one pill besides circles). */
export function Switch({ x, y, on = true, dark = false }: { x: number; y: number; on?: boolean; dark?: boolean }) {
  return (
    <g>
      <rect x={x} y={y} width="17" height="9.4" rx="4.7" fill={on ? (dark ? D.brand : L.brand) : dark ? D.lineStrong : 'rgba(17,18,20,.2)'} />
      <circle cx={on ? x + 12.4 : x + 4.6} cy={y + 4.7} r="3.2" fill="#ffffff" />
    </g>
  );
}

/** A lucide icon drawn at `size` inside the scene (the kit's own icon set). */
export function Lu({
  icon: Icon,
  x,
  y,
  size = 8,
  color = L.ink2,
  sw = 2,
}: {
  icon: LucideIcon;
  x: number;
  y: number;
  size?: number;
  color?: string;
  sw?: number;
}) {
  return <Icon x={x} y={y} width={size} height={size} stroke={color} strokeWidth={sw} fill="none" />;
}
